
import { supabase } from "@/integrations/supabase/client";
import { toast } from 'sonner';
import { getErrorMessage } from "@/utils/errorHandling";
import { CircuitBreaker } from "@/utils/resilience/circuitBreaker";

// Cache for tool agent responses
const responseCache: Record<string, { data: any; timestamp: number }> = {};

// Create circuit breakers for each tool agent
const circuitBreakers: Record<string, CircuitBreaker> = {};

// Initialize circuit breakers for default tools
['microsoft-exchange', 'linkedin', 'elevenlabs'].forEach(tool => {
  circuitBreakers[tool] = new CircuitBreaker({
    failureThreshold: 5,
    resetTimeout: 60000, // 1 minute
    fallbackFunction: async (toolName, method, params) => {
      console.log(`Circuit open for ${toolName}, using fallback`);
      
      // Try to use cached response if available
      const cacheKey = `${toolName}-${method}-${JSON.stringify(params)}`;
      const cachedResponse = responseCache[cacheKey];
      
      if (cachedResponse && (Date.now() - cachedResponse.timestamp < 3600000)) { // Cache valid for 1 hour
        console.log(`Using cached response for ${toolName}.${method}`);
        return cachedResponse.data;
      }
      
      // Return a default response if no cache available
      return {
        status: 'degraded_service',
        message: `${toolName} service is currently experiencing issues. Please try again later.`,
        data: null
      };
    }
  });
});

// Type for the MCP request
interface MCPRequest {
  requestId: string;
  tenantId: string;
  userId: string;
  method: string;
  params: Record<string, any>;
}

/**
 * Call a tool agent with circuit breaker pattern
 */
export async function callToolAgent<T>(
  toolName: string,
  method: string,
  params: Record<string, any> = {}
): Promise<T> {
  // Get or create circuit breaker for this tool
  if (!circuitBreakers[toolName]) {
    circuitBreakers[toolName] = new CircuitBreaker({
      failureThreshold: 5,
      resetTimeout: 60000,
      fallbackFunction: async (tName, mtd, prms) => { // Ensure fallback function signature matches execute
        console.warn(`Circuit open for ${tName}, using fallback for method ${mtd}`);
        
        const cacheKeyFallback = `${tName}-${mtd}-${JSON.stringify(prms)}`;
        const cachedResponseFallback = responseCache[cacheKeyFallback];
        
        if (cachedResponseFallback && (Date.now() - cachedResponseFallback.timestamp < 3600000)) {
          console.log(`Using cached response for ${tName}.${mtd} from fallback`);
          return cachedResponseFallback.data;
        }
        
        const fallbackResponse = {
          status: 'degraded_service',
          message: `${tName} service is currently experiencing issues due to circuit breaker. Please try again later.`,
          data: null
        };

        // Log system alert for circuit breaker fallback
        try {
          const currentUserIdVal = await getCurrentUserId();
          await supabase.functions.invoke('log-system-alert', {
            body: {
              source: `mcp_tool_orchestrator:circuit_breaker`,
              type: 'CIRCUIT_BREAKER_FALLBACK',
              severity: 'WARNING',
              message: `Circuit breaker triggered for ${tName}.${mtd}. Using fallback.`,
              details: { toolName: tName, method: mtd, params: prms, fallbackResponse },
              user_id: currentUserIdVal
            }
          });
        } catch (alertError) {
          console.error("Failed to log system alert for circuit breaker fallback:", alertError);
        }
        return fallbackResponse;
      }
    });
  }

  // Generate request ID
  const requestId = `req_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

  // Create cache key
  const cacheKey = `${toolName}-${method}-${JSON.stringify(params)}`;
  const currentUserIdVal = await getCurrentUserId(); // Get user ID once
  
  try {
    // Check feature flag
    const isMCPEnabled = await checkFeatureEnabled('mcp_enabled');
    if (!isMCPEnabled) {
      const featureDisabledMsg = `${toolName} integration is not enabled for your account. Feature 'mcp_enabled' is off.`;
      toast.error(featureDisabledMsg);
      // Log system alert for feature disabled
      try {
        await supabase.functions.invoke('log-system-alert', {
          body: {
            source: `mcp_tool_orchestrator:feature_check`,
            type: 'FEATURE_DISABLED',
            severity: 'WARNING',
            message: featureDisabledMsg,
            details: { toolName, method, params, featureName: 'mcp_enabled' },
            user_id: currentUserIdVal
          }
        });
      } catch (alertError) {
        console.error("Failed to log system alert for feature disabled:", alertError);
      }
      throw new Error(featureDisabledMsg);
    }

    // Execute the call using circuit breaker
    const response = await circuitBreakers[toolName].execute(
      async () => {
        console.log(`Calling ${toolName} agent with method: ${method}`);
        
        const currentTenant = await getCurrentTenant(); // Renamed to avoid conflict
        
        const mcpRequest: MCPRequest = {
          requestId,
          tenantId: currentTenant || "default_tenant_orchestrator", // More specific default
          userId: currentUserIdVal || "anonymous_orchestrator", // More specific default
          method,
          params
        };
        
        const { data, error } = await supabase.functions.invoke(toolName, {
          body: mcpRequest
        });
        
        if (error) {
          // Log system alert for supabase.functions.invoke error inside circuit breaker
           try {
            await supabase.functions.invoke('log-system-alert', {
              body: {
                source: `mcp_tool_orchestrator:invoke_error`,
                type: 'TOOL_AGENT_INVOCATION_FAILURE',
                severity: 'ERROR',
                message: `Error invoking tool agent ${toolName}.${method}: ${getErrorMessage(error)}`,
                details: { toolName, method, params, error: { message: getErrorMessage(error), ...error } },
                user_id: currentUserIdVal
              }
            });
          } catch (alertErrorInner) {
            console.error("Failed to log system alert for tool agent invocation error:", alertErrorInner);
          }
          throw error; // Re-throw to be caught by circuit breaker logic
        }
        
        // Cache successful responses
        responseCache[cacheKey] = {
          data,
          timestamp: Date.now()
        };
        
        return data;
      },
      toolName, // Pass toolName for fallback
      method,   // Pass method for fallback
      params    // Pass params for fallback
    );

    return response as T;
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    console.error(`Error calling ${toolName}.${method}:`, errorMessage, error);
    // Don't toast here if circuit breaker or feature flag already toasted.
    // The error might be re-thrown by circuit breaker, or it's an initial error
    // before circuit breaker execution (like feature flag).
    // System alert for general errors in callToolAgent (outside circuit breaker execution, e.g. checkFeatureEnabled)
    // is already handled if it's from feature disabled. If it's another error, it will be caught by useMCPToolAgent.

    // However, if it's an error *not* from feature disabled and *not* from circuit breaker (which should be rare),
    // it could be logged here. But useMCPToolAgent already logs errors.
    // The main new logging point here is for feature_disabled and circuit_breaker_fallback.
    throw new Error(errorMessage);
  }
}

// Helper function to check if a feature is enabled
async function checkFeatureEnabled(featureName: string): Promise<boolean> {
  try {
    const { data, error } = await supabase.functions.invoke('check-feature-enabled', {
      body: {
        feature_name: featureName
      }
    });

    if (error) {
      // Log system alert for check-feature-enabled failure
      try {
        const currentUserIdVal = await getCurrentUserId();
        await supabase.functions.invoke('log-system-alert', {
          body: {
            source: `mcp_tool_orchestrator:check_feature_enabled`,
            type: 'FUNCTION_INVOCATION_ERROR',
            severity: 'ERROR',
            message: `Failed to invoke 'check-feature-enabled' for feature '${featureName}': ${getErrorMessage(error)}`,
            details: { featureName, error: { message: getErrorMessage(error), ...error } },
            user_id: currentUserIdVal
          }
        });
      } catch (alertError) {
        console.error("Failed to log system alert for checkFeatureEnabled error:", alertError);
      }
      throw error; // Rethrow to be handled by caller
    }
    return data?.enabled ?? false;
  } catch (error) {
    console.error(`Error checking feature flag '${featureName}':`, getErrorMessage(error));
    // Return false and let the caller handle/log the specific error if needed
    // The alert for the specific invocation failure is logged above.
    return false; 
  }
}

// Helper function to get current tenant ID
async function getCurrentTenant(): Promise<string | null> { // Made async to align with getCurrentUserId
  // Implement tenant ID retrieval logic. For now, using localStorage as an example.
  // In a real app, this might come from context or a user session object.
  return localStorage.getItem('tenant_id');
}

// Helper function to get current user ID
async function getCurrentUserId(): Promise<string | null> {
  try {
    const { data: { user } , error } = await supabase.auth.getUser();
    if (error) {
      // console.warn("Error getting user ID in toolOrchestrator:", error.message);
      return null;
    }
    return user?.id || null;
  } catch (error) {
    console.error("Exception getting user ID in toolOrchestrator:", getErrorMessage(error));
    return null;
  }
}

/**
 * Get billing plan for current tenant
 */
export async function getCurrentBillingPlan(): Promise<string> {
  try {
    const { data, error } = await supabase.functions.invoke('get-tenant-billing-plan');
    
    if (error) {
       // Log system alert for get-tenant-billing-plan failure
      try {
        const currentUserIdVal = await getCurrentUserId();
        await supabase.functions.invoke('log-system-alert', {
          body: {
            source: `mcp_tool_orchestrator:get_billing_plan`,
            type: 'FUNCTION_INVOCATION_ERROR',
            severity: 'WARNING',
            message: `Failed to invoke 'get-tenant-billing-plan': ${getErrorMessage(error)}`,
            details: { error: { message: getErrorMessage(error), ...error } },
            user_id: currentUserIdVal
          }
        });
      } catch (alertError) {
        console.error("Failed to log system alert for getTenantBillingPlan error:", alertError);
      }
      throw error;
    }
    return data?.billing_plan || "free";
  } catch (error) {
    console.error("Error getting billing plan:", getErrorMessage(error));
    return "free"; // Default to free on error
  }
}
